import type { AxiosResponse } from 'axios'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { useCustomerStore } from './customers'
import { useShippingLinesStore } from './shippingLines'
import { useTrucksStore } from './trucks'
import { useUserStore } from './user'
import Notification from '~/services/notificationService'
import type { ContainerFilters } from '~/compositions/containerFilters'
import type { Appointment } from '~/models/appointments'
import type { ImportStatus } from '~/models/importStatus'
import type { LineImportStatus } from '~/models/lineImportStatus'
import type { PierPass } from '~/models/pierPass'
import type {
  AppointmentsCheckedNotification,
  GETContainer,
  ImportStatusCheckedNotification,
  POSTContainerWatch,
  POSTUploadContainersResponse,
  SimpleUser,
  TerminalName,
} from '~/services/apiClient'
import type { LoadContainerOptions } from '~/services/apiHelpers'
import { useGlobalFilters } from '~/compositions/globalFilters'
import { containerIsOnGreenTruck } from '~/logic/greenTrucks'
import { Container } from '~/models/containers'
import {
  ContainersApi,
  ContainerWatchState,
  PortComplex,
} from '~/services/apiClient'
import { handleSkippedAPIError } from '~/apiErrorHandling'
import { NO_RESPONSE_RETRY_MS, retryOnNoResponse } from '~/noResponseRetry'
import { isRequestAbort } from '~/requestAbort'
import { loadContainers } from '~/services/apiHelpers'
import { fastParseISO } from '~/utils'

function stoppedByUs(error: unknown, signal: AbortSignal): boolean {
  return isRequestAbort(error) || signal.aborted
}

/**
 * The green-truck half of the live-update filter check.
 *
 * It can't sit in `Container.matchesFilters` with the other filters: resolving green
 * status needs the truck roster, and a model has no business reaching into a store for
 * it. Unresolvable green status counts as not green, so an update we can't vouch for
 * stays out of a green-filtered list — a container already in the list keeps updating
 * either way, via the `containerLookup` check at the call site.
 */
function matchesGreenTruckFilter(
  container: Container,
  filters: ContainerFilters
): boolean {
  if (!filters.is_green_truck) return true
  return containerIsOnGreenTruck(container, useTrucksStore().getTruck)
}

export const useContainerStore = defineStore('containers', {
  state: () => {
    return {
      loading: false,
      preLoading: false,
      containers: [] as Container[],
      containerLookup: new Map() as Map<string, Container>,
      containerLookupByTerminal: new Map() as Map<TerminalName, Container[]>,
      // For live updating "last_checked" fields
      importStatusLookup: new Map() as Map<number, ImportStatus>,
      pierPassLookup: new Map() as Map<number, PierPass>,
      lineStatusLookup: new Map() as Map<number, LineImportStatus>,
      appointmentsLookup: new Map() as Map<number, Appointment>,
      loadContainersAbortController: null as AbortController | null,
      loadStateCountsAbortController: null as AbortController | null,
      loadTerminalCountsAbortController: null as AbortController | null,
      focusedContainer: null as Container | null,
      requestedInitialTab: null as string | null,
      // Keys are container numbers
      selectedContainers: new Set<string>(),
      // This key is used to force re-rendering of checkboxes whenever the selection
      // changes
      selectionKey: 0,
      searchedButUnwatchedContainers: [] as string[],
      containersWatchedAtOtherPorts: [] as string[],
      lastUsedFilters: undefined as LoadContainerOptions | undefined,

      executingWatchContainers: false,
      statusCounts: new Map() as Map<ContainerWatchState, number | null>,
      countsByTerminal: new Map() as Map<TerminalName, number>,
    }
  },
  actions: {
    clearAllData() {
      this.containers = []
      this.containerLookup = new Map()
      this.importStatusLookup = new Map()
      this.pierPassLookup = new Map()
      this.lineStatusLookup = new Map()
      this.appointmentsLookup = new Map()
      this.containerLookupByTerminal = new Map()
      this.focusedContainer = null
    },
    cancelExistingRequests(): AbortController {
      if (this.loadContainersAbortController) {
        this.loadContainersAbortController.abort()
      }
      const abortController = new AbortController()
      this.loadContainersAbortController = abortController
      return abortController
    },
    load(
      originalOptions: LoadContainerOptions,
      followOnLoading = false
    ): Promise<void> {
      const options = { ...originalOptions }
      const newAbortController = this.cancelExistingRequests()
      if (!followOnLoading) {
        this.preLoading = true
      }
      this.loading = true
      this.lastUsedFilters = options
      const pageSize = options.pageSize
      if (options.preloadSize !== undefined) {
        options.pageSize = options.preloadSize
        // Set explicit offset for the quick load to handle pages 2+
        // The API calculates offset as page_size * (page - 1), but we need to use
        // the full page size, not the preload page size.
        // This is how we get proper pagination when preloading.
        options.offset = (originalOptions.page - 1) * originalOptions.pageSize
      }
      return retryOnNoResponse(
        () =>
          loadContainers({
            ...options,
            abortController: this.loadContainersAbortController || undefined,
            port_complexes: useGlobalFilters().portComplexes.value,
            skipErrorHandling: true,
          }),
        {
          extraAttempts: 1,
          delayMs: () => NO_RESPONSE_RETRY_MS,
          signal: newAbortController.signal,
        }
      )
        .then((resp) => {
          // Replace only once the new rows are in hand. Clearing at the start
          // left the page blank when this call was stopped or died.
          if (!followOnLoading) {
            this.clearAllData()
            this.selectedContainers = new Set()
          }
          this.addContainers(resp.data.containers)
          this.searchedButUnwatchedContainers = resp.data.unwatched_containers
          this.containersWatchedAtOtherPorts =
            resp.data.containers_watched_at_other_ports
          this.loadContainersAbortController = null
          if (options.preloadSize !== undefined) {
            // Completed pre-load case
            this.preLoading = false
            if (resp.data.containers.length === options.preloadSize) {
              options.pageSize = pageSize - options.preloadSize
              options.offset =
                options.preloadSize +
                (originalOptions.page - 1) * originalOptions.pageSize
              options.preloadSize = undefined
              this.load(options, true)
            } else {
              this.loading = false
              this.preLoading = false
            }
          } else {
            // Completed full load case
            this.loading = false
            this.preLoading = false
          }
        })
        .catch(async (err) => {
          if (stoppedByUs(err, newAbortController.signal)) {
            return
          }
          this.loading = false
          this.preLoading = false
          // Only a real logout stays quiet. A 401 that did not log out still fails.
          if (await handleSkippedAPIError(err)) {
            return
          }
          throw err
        })
        .finally(() => {
          if (this.loadContainersAbortController === newAbortController) {
            this.loadContainersAbortController = null
          }
        })
    },
    parseContainers(rawContainers: GETContainer[]) {
      const userStore = useUserStore()
      const lineLFDGapConfig = useShippingLinesStore().lineLFDGapConfig
      const containers = rawContainers.map(
        (rawContainer) =>
          new Container(rawContainer, userStore.demo_mode, lineLFDGapConfig)
      )
      return containers
    },
    /**
     * Rebuild every held container from the raw payload it was parsed from.
     *
     * Only needed because the line-LFD gap config is a constructor input (badges are
     * built eagerly), and `/containers/shipping_lines/` can answer after the
     * containers themselves have loaded. Callers watch the config and call this;
     * without it the CHECK LINE FOR LFD badge would be missing for whoever lost that
     * race, until their next live update.
     */
    reparseContainers() {
      if (this.containerLookup.size === 0) return
      this.addContainers(
        [...this.containerLookup.values()].map(
          (container) => container.raw_data
        )
      )
    },
    addContainers(rawContainers: GETContainer[]) {
      const containers = this.parseContainers(rawContainers)
      this.addParsedContainers(containers)
    },
    addParsedContainers(containers: Container[]) {
      for (const container of containers) {
        // Update internal object lookups
        for (const importStatus of container.import_statuses) {
          this.importStatusLookup.set(importStatus.cycle_id, importStatus)
        }
        if (container.line_import_status) {
          this.lineStatusLookup.set(
            container.line_import_status.id,
            container.line_import_status
          )
        }
        if (container.pier_pass) {
          this.pierPassLookup.set(container.pier_pass.id, container.pier_pass)
        }
        if (container.clean_truck_fee) {
          this.pierPassLookup.set(
            container.clean_truck_fee.id,
            container.clean_truck_fee
          )
        }
        if (container.latest_appointment) {
          this.appointmentsLookup.set(
            container.latest_appointment.id,
            container.latest_appointment
          )
        }
        // Update container
        const existingContainer = this.containerLookup.get(container.number)
        if (existingContainer) {
          container.incrementFrontendVersion(
            existingContainer.frontendVersion + 1
          )
        }
        this.containerLookup.set(container.number, container)
        if (container.terminal) {
          if (!this.containerLookupByTerminal.has(container.terminal)) {
            this.containerLookupByTerminal.set(container.terminal, [])
          }
          this.containerLookupByTerminal
            .get(container.terminal)!
            .push(container)
        }
      }
      this.refreshContainersView()
    },
    removeContainers(containerNumbers: string[]) {
      const api = new ContainersApi()
      api
        .stopWatchingContainersContainersUnwatchPost(containerNumbers)
        .then(() => {
          Notification.success(`Removed ${containerNumbers.length} containers`)
          for (const containerNumber of containerNumbers) {
            this.containerLookup.delete(containerNumber)
          }
          this.refreshContainersView()
        })
    },
    refreshContainersView() {
      this.containers = [...this.containerLookup.values()]
    },
    updateContainerNoteBody(containerNumber: string, noteBody: string | null) {
      const container = this.containerLookup.get(containerNumber)
      if (container) container.note_body = noteBody
    },
    updateContainer(rawContainer: GETContainer) {
      const container = this.parseContainers([rawContainer])[0]
      // Add to our store if this matches our filters OR the container is already in our
      // store
      const { terminalMatchesPortComplexes } = useGlobalFilters()
      if (
        container.terminal &&
        !terminalMatchesPortComplexes(container.terminal)
      ) {
        return
      }

      // For sub-carriers not searching specific containers, filter to containers
      // assigned to them. This prevents sub-carriers from seeing websocket updates
      // for containers they weren't originally assigned.
      const userStore = useUserStore()
      let effectiveFilters = this.lastUsedFilters
      if (
        userStore.isSubCarrier &&
        userStore.loggedInUser &&
        effectiveFilters &&
        !effectiveFilters.container_numbers &&
        !effectiveFilters.assigned_user_ids
      ) {
        effectiveFilters = {
          ...effectiveFilters,
          assigned_user_ids: [userStore.loggedInUser.id],
        }
      }
      // if there aren't last used filters, the container won't be added
      if (
        (effectiveFilters &&
          container.matchesFilters(effectiveFilters) &&
          matchesGreenTruckFilter(container, effectiveFilters)) ||
        this.containerLookup.has(container.number)
      ) {
        // eslint-disable-next-line no-console
        console.log(
          `Updating container ${container.number} that matches ` +
            `filters ${JSON.stringify(effectiveFilters)}`
        )
        this.addParsedContainers([container])
      }
    },
    // Non-destructively refetch a single container in place (e.g. after a
    // reschedule completes) so drawers reading it show the new appointment
    // without a full list reload. `addParsedContainers` refreshes the
    // lookup-tracked view (dispatch drawer); the imports page's
    // `focusedContainer` is detached state, so re-point it explicitly.
    async refreshContainerByNumber(containerNumber: string): Promise<void> {
      const resp = await loadContainers({
        container_numbers: containerNumber,
        page: 1,
        pageSize: 1,
        port_complexes: useGlobalFilters().portComplexes.value,
      })
      this.addContainers(resp.data.containers)
      if (this.focusedContainer?.number === containerNumber) {
        this.focusedContainer =
          this.containerLookup.get(containerNumber) ?? this.focusedContainer
      }
    },
    updateImportStatusLastObservedTimes(
      notification: ImportStatusCheckedNotification
    ) {
      for (const [rawImportStatusCycleID, rawObserved] of Object.entries(
        notification.last_checked_by_import_status_ids
      )) {
        const importStatusCycleID = Number.parseInt(rawImportStatusCycleID)
        const observed = fastParseISO(rawObserved)
        const loadedImportStatus =
          this.importStatusLookup.get(importStatusCycleID)
        if (loadedImportStatus) {
          loadedImportStatus.last_observed = observed
        }
      }
    },
    updatePierPassLastObservedTimes(
      notification: ImportStatusCheckedNotification
    ) {
      for (const [rawPierPassID, rawObserved] of Object.entries(
        notification.last_checked_by_import_status_ids
      )) {
        const pierPassID = Number.parseInt(rawPierPassID)
        const observed = fastParseISO(rawObserved)
        const loadedPierPass = this.pierPassLookup.get(pierPassID)
        if (loadedPierPass) {
          loadedPierPass.last_observed = observed
        }
      }
    },
    updateLineStatusLastObservedTimes(
      notification: ImportStatusCheckedNotification
    ) {
      for (const [rawLineStatusID, rawObserved] of Object.entries(
        notification.last_checked_by_import_status_ids
      )) {
        const lineStatusID = Number.parseInt(rawLineStatusID)
        const observed = fastParseISO(rawObserved)
        const loadedLineStatus = this.lineStatusLookup.get(lineStatusID)
        if (loadedLineStatus) {
          loadedLineStatus.last_observed = observed
        }
      }
    },
    updateAppointmentLastObservedTimes(
      notification: AppointmentsCheckedNotification
    ) {
      for (const [rawAppointmentID, rawObserved] of Object.entries(
        notification.last_checked_by_appointment_ids
      )) {
        const appointmentID = Number.parseInt(rawAppointmentID)
        const observed = fastParseISO(rawObserved)
        const loadedAppointment = this.appointmentsLookup.get(appointmentID)
        if (loadedAppointment) {
          // eslint-disable-next-line no-console
          console.log(
            `Updating appointment ${appointmentID} for ` +
              `${loadedAppointment.container_number} last observed ${observed}`
          )
          loadedAppointment.last_observed = observed
        }
      }
    },
    loadStateCounts({
      vessels,
      vessel_eta,
      vessel_eta_is_unknown,
      tags,
      show_not_tagged,
      is_green_truck,
      needs_line_lfd_check,
      terminal,
      excluded_customer_ids,
      container_numbers,
      auto_book_on,
      auto_book_off,
      container_types,
      shipping_lines,
      hold_types,
      assigned_user_ids,
      updated_in_last_minutes,
    }: ContainerFilters) {
      if (this.loadStateCountsAbortController) {
        this.loadStateCountsAbortController.abort()
      }
      const abortController = new AbortController()
      this.loadStateCountsAbortController = abortController
      const api = new ContainersApi()
      const portComplexes = useGlobalFilters().portComplexes.value
      return retryOnNoResponse(
        () =>
          api.getContainerCountsByStateContainersStateCountsGet(
            terminal,
            vessels,
            vessel_eta,
            vessel_eta_is_unknown,
            undefined, // customer_ids — Imports hides customers instead
            excluded_customer_ids,
            undefined, // customer_id (singular)
            auto_book_on,
            auto_book_off,
            container_types,
            shipping_lines,
            hold_types,
            updated_in_last_minutes,
            tags,
            show_not_tagged,
            is_green_truck,
            needs_line_lfd_check,
            container_numbers,
            assigned_user_ids,
            portComplexes,
            {
              signal: abortController.signal,
              skipErrorHandling: true,
            }
          ),
        {
          extraAttempts: 1,
          delayMs: () => NO_RESPONSE_RETRY_MS,
          signal: abortController.signal,
        }
      )
        .then((resp) => {
          this.statusCounts = new Map(resp.data.map((x) => [x.state, x.count]))
        })
        .catch(async (err) => {
          if (stoppedByUs(err, abortController.signal)) {
            return
          }
          if (err?.response) {
            // A real HTTP error keeps its normal popup or logout. A dropped
            // call stays quiet because the list call shows the popup.
            if (await handleSkippedAPIError(err)) {
              return
            }
          }
          throw err
        })
        .finally(() => {
          if (this.loadStateCountsAbortController === abortController) {
            this.loadStateCountsAbortController = null
          }
        })
    },
    loadByTerminalCounts({
      vessels,
      vessel_eta,
      vessel_eta_is_unknown,
      tags,
      show_not_tagged,
      is_green_truck,
      needs_line_lfd_check,
      states,
      excluded_customer_ids,
      container_numbers,
      container_types,
      shipping_lines,
      hold_types,
      auto_book_on,
      auto_book_off,
      assigned_user_ids,
      updated_in_last_minutes,
    }: ContainerFilters) {
      if (this.loadTerminalCountsAbortController) {
        this.loadTerminalCountsAbortController.abort()
      }
      const abortController = new AbortController()
      this.loadTerminalCountsAbortController = abortController
      const api = new ContainersApi()
      const portComplexes = useGlobalFilters().portComplexes.value
      return retryOnNoResponse(
        () =>
          api.getContainerCountsByTerminalContainersTerminalCountsGet(
            states,
            undefined,
            undefined,
            vessels,
            vessel_eta,
            vessel_eta_is_unknown,
            undefined, // customer_ids — Imports hides customers instead
            excluded_customer_ids,
            undefined, // customer_id (singular)
            auto_book_on,
            auto_book_off,
            container_types,
            shipping_lines,
            hold_types,
            updated_in_last_minutes,
            tags,
            show_not_tagged,
            is_green_truck,
            needs_line_lfd_check,
            container_numbers,
            assigned_user_ids,
            portComplexes,
            {
              signal: abortController.signal,
              skipErrorHandling: true,
            }
          ),
        {
          extraAttempts: 1,
          delayMs: () => NO_RESPONSE_RETRY_MS,
          signal: abortController.signal,
        }
      )
        .then((resp) => {
          this.countsByTerminal = new Map(
            resp.data.map((x) => [x.terminal, x.count])
          )
        })
        .catch(async (err) => {
          if (stoppedByUs(err, abortController.signal)) {
            return
          }
          if (err?.response) {
            // A real HTTP error keeps its normal popup or logout. A dropped
            // call stays quiet because the list call shows the popup.
            if (await handleSkippedAPIError(err)) {
              return
            }
          }
          throw err
        })
        .finally(() => {
          if (this.loadTerminalCountsAbortController === abortController) {
            this.loadTerminalCountsAbortController = null
          }
        })
    },
    watchUnwatchedContainers(
      assignedUsers: SimpleUser[],
      customerID: number | undefined,
      tags: string[],
      portComplex: PortComplex
    ): Promise<AxiosResponse<POSTUploadContainersResponse>> {
      const newWatches = this.searchedButUnwatchedContainers.map(
        (containerNumber) => {
          return {
            container_number: containerNumber,
            customer_id: customerID,
            tags,
            port_complex: portComplex,
          }
        }
      )
      return this.watchContainers(newWatches, assignedUsers, customerID).then(
        (response) => {
          this.searchedButUnwatchedContainers = response.data.errors.map(
            (error) => error.container_number
          )
          return response
        }
      )
    },
    watchContainers(
      watches: POSTContainerWatch[],
      assignedUsers: SimpleUser[],
      customerID: number | undefined,
      cycleState: ContainerWatchState | undefined = undefined
    ): Promise<AxiosResponse<POSTUploadContainersResponse>> {
      this.executingWatchContainers = true
      const assignedUserIDs = assignedUsers.map((u) => u.id)
      const api = new ContainersApi()
      const watchPromise = api.watchContainersContainersWatchPost(
        watches,
        assignedUserIDs
      )
      watchPromise
        .then((response) => {
          if (response.data.container_numbers.length > 0) {
            const userStore = useUserStore()
            const customerStore = useCustomerStore()
            for (const watch of watches) {
              if (watch.container_number in response.data.container_numbers) {
                this.addContainers([
                  {
                    container_number: watch.container_number,
                    cycle_state: cycleState || ContainerWatchState.Unknown,
                    import_statuses: [],
                    auto_book_ignoring_capacity: false,
                    auto_book_on: false,
                    assigned_users: assignedUsers,
                    added_by_user: userStore.loggedInUser || undefined,
                    customer:
                      customerID !== undefined
                        ? customerStore.getByCustomerID(customerID)
                        : undefined,
                    other_appointment_counts: [],
                    tags: [],
                    watched_time: DateTime.now().toISO(),
                    port_complex: watch.port_complex ?? PortComplex.LaLb,
                  },
                ])
              }
            }
          }
        })
        .finally(() => {
          this.executingWatchContainers = false
        })
      return watchPromise
    },
    // Selections
    clearContainerSelection() {
      let hasSelectedContainers = false
      for (const container of this.containers) {
        if (this.selectedContainers.has(container.number)) {
          hasSelectedContainers = true
        }
        this.selectedContainers.delete(container.number)
      }
      // Only trigger a re-render of checkboxes if there were any selected checkboxes
      // to clear
      if (hasSelectedContainers) {
        this.selectionKey += 1
      }
    },
    selectAllContainers() {
      for (const container of this.containers) {
        this.selectedContainers.add(container.number)
      }
      this.selectionKey += 1
    },
    toggleContainerSelection(containerNumber: string) {
      if (this.selectedContainers.has(containerNumber)) {
        this.selectedContainers.delete(containerNumber)
      } else {
        this.selectedContainers.add(containerNumber)
      }
      this.selectionKey += 1
    },
  },
  getters: {
    numUnwatchedContainers(): number {
      return this.searchedButUnwatchedContainers?.length ?? 0
    },
    numContainersWatchedAtOtherPorts(): number {
      return this.containersWatchedAtOtherPorts?.length ?? 0
    },
    isContainerSelected() {
      return (container: string): boolean => {
        return this.selectedContainers.has(container)
      }
    },
    selectedContainerNumbers(): string[] {
      return [...this.selectedContainers]
    },
    selectedContainerObjects(): Container[] {
      const containers = []
      for (const containerNumber of this.selectedContainerNumbers) {
        const container = this.containerLookup.get(containerNumber)
        if (container) {
          containers.push(container)
        }
      }
      return containers
    },
    assignedUsersForSelectedContainers(): SimpleUser[] {
      const assignedUsers = new Map()
      for (const container of this.selectedContainerObjects) {
        for (const user of container.assigned_users) {
          assignedUsers.set(user.id, user)
        }
      }
      return [...assignedUsers.values()]
    },
    selectedContainerAppointments(): Appointment[] {
      return this.selectedContainerObjects
        .filter((c) => c.is_in_booked_state && c.latest_appointment)
        .map((c) => c.latest_appointment!)
    },
    numAppointedContainersForSelection(): number {
      return this.selectedContainerAppointments.length
    },
  },
})
